import { Head, router, usePage } from "@inertiajs/react"
import {
  Check,
  Dices,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"

import imperativeIcon from "@/assets/imperative_icon.svg"
import { OnboardingLogout } from "@/components/onboarding/logout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { SiriOrb } from "@/components/ui/siri-orb"
import {
  ASSISTANT_THEMES,
  assistantTheme,
  rollAssistantName,
  themeForBrandColor,
} from "@/lib/assistant"
import { cn } from "@/lib/utils"
import {
  destroyPartnerUserSessionPath,
  partnerOnboardingBrandPath,
  partnerOnboardingFinishPath,
  partnerOnboardingIdentityPath,
  partnerOnboardingMarkupsPath,
  partnerOnboardingStripeContinuePath,
  partnerSettingsStripeAccountPath,
} from "@/routes"

// IMP-420: the guided first-run partner onboarding wizard. Ported from the
// design prototype (/design/partner-onboarding) minus its self-serve signup step
// (out of scope — partners are invite-based), wired to real persistence. The
// current step is server-driven (props.step, resumed from the partner account);
// each step's Continue posts to the matching onboarding endpoint, which persists
// and advances, and the page re-renders at the next step.

type Step = "brand" | "identity" | "stripe" | "markups" | "admins" | "complete"
// IMP-464: Identity sits second, between Brand and Stripe — five steps.
const WIZARD_STEPS: Step[] = [
  "brand",
  "identity",
  "stripe",
  "markups",
  "admins",
]

function stepNumber(step: Step): number {
  return WIZARD_STEPS.indexOf(step) + 1
}

// The per-agent override shape the pricing prop still carries; overrides are
// configured in partner Settings now, not in onboarding (IMP-420 follow-up).
type OverrideType = "percentage" | "fixed"

// ---------------------------------------------------------------------------
// Shell — full Imperative lockup (IMP-464: not the "Partner Program" side-
// program treatment; the wordmark is foreground-colored text so it holds up
// in dark mode, where a fixed dark SVG would vanish), a "Step N of 5" pill,
// a large title + subtitle.
// ---------------------------------------------------------------------------
function OnboardingShell({
  step,
  title,
  subtitle,
  width = "max-w-xl",
  children,
}: {
  step: Step
  title: React.ReactNode
  subtitle?: React.ReactNode
  width?: string
  children: React.ReactNode
}) {
  return (
    // `relative` anchors OnboardingLogout's mobile "…" menu.
    <div className="bg-background relative flex min-h-svh flex-col">
      <OnboardingLogout sessionPath={destroyPartnerUserSessionPath()} />
      <div className="flex items-center justify-center px-4 py-5">
        <div className="flex items-center gap-2.5">
          <img
            src={imperativeIcon}
            alt=""
            className="size-8 rounded-lg object-cover shadow-sm"
          />
          <span className="text-foreground text-xl font-semibold tracking-[0.18em] uppercase">
            Imperative
          </span>
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center px-6 pt-11 pb-12">
        <div className={cn("flex w-full flex-col items-center", width)}>
          <div className="mb-5">
            <span className="bg-muted text-foreground inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold">
              Step {stepNumber(step)} of {WIZARD_STEPS.length}
            </span>
          </div>
          <div className="mb-8 space-y-2 text-center">
            <h1 className="text-3xl/10 font-normal tracking-tight">{title}</h1>
            {subtitle && (
              <p className="text-muted-foreground max-w-2xl text-base/7">
                {subtitle}
              </p>
            )}
          </div>
          {children}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 1 — Brand
// ---------------------------------------------------------------------------
interface BrandProps {
  name: string | null
  subdomain: string | null
  subdomain_suffix: string | null
  logo_url: string | null
  logo_wordmark_url: string | null
}

// The subdomain accepts only lowercase letters, numbers, and hyphens; spaces and
// any other characters are dropped as the user types (matching the server's
// format rule), so "Acme Co." becomes "acmeco".
function slugifySubdomain(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9-]/g, "")
}

function StepBrand({ brand }: { brand: BrandProps }) {
  const [name, setName] = useState(brand.name ?? "")
  // Prefill from the saved subdomain, else derive one from the business name.
  const [subdomain, setSubdomain] = useState(
    brand.subdomain ?? slugifySubdomain(brand.name ?? ""),
  )
  // Once the partner edits the subdomain themselves, stop auto-filling it from
  // the business name.
  const [subdomainEdited, setSubdomainEdited] = useState(
    (brand.subdomain ?? "") !== "",
  )
  const [iconFile, setIconFile] = useState<File | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [iconPreview, setIconPreview] = useState<string | null>(brand.logo_url)
  const [logoPreview, setLogoPreview] = useState<string | null>(
    brand.logo_wordmark_url,
  )
  const [saving, setSaving] = useState(false)
  const iconRef = useRef<HTMLInputElement>(null)
  const logoRef = useRef<HTMLInputElement>(null)

  function onNameChange(value: string) {
    setName(value)
    // Keep the subdomain in sync with the name until the partner overrides it.
    if (!subdomainEdited) setSubdomain(slugifySubdomain(value))
  }

  function onSubdomainChange(value: string) {
    setSubdomainEdited(true)
    setSubdomain(slugifySubdomain(value))
  }

  function submit() {
    const data = new FormData()
    data.append("partner[name]", name)
    data.append("partner[subdomain]", subdomain)
    if (iconFile) data.append("partner[logo]", iconFile)
    if (logoFile) data.append("partner[logo_wordmark]", logoFile)
    router.patch(partnerOnboardingBrandPath(), data, {
      preserveScroll: true,
      onStart: () => setSaving(true),
      onFinish: () => setSaving(false),
    })
  }

  const errors = (usePage().props.errors ?? {}) as Record<string, string>

  return (
    <OnboardingShell
      step="brand"
      title="Set up your brand"
      subtitle="This is how your workspace appears to you and to the clients you resell to. You can change it any time in Settings."
    >
      <div className="w-full space-y-6">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="partner-name">Business name</Label>
          <Input
            id="partner-name"
            autoFocus
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
          />
          {errors.name && (
            <p className="text-destructive text-sm">{errors.name}</p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="partner-subdomain">Subdomain</Label>
          <div className="flex items-center">
            <Input
              id="partner-subdomain"
              className={cn(brand.subdomain_suffix && "rounded-r-none")}
              value={subdomain}
              placeholder="acme"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              onChange={(e) => onSubdomainChange(e.target.value)}
            />
            {brand.subdomain_suffix && (
              <span className="border-input bg-muted text-muted-foreground inline-flex h-9 items-center rounded-r-md border border-l-0 px-3 text-sm">
                .{brand.subdomain_suffix}
              </span>
            )}
          </div>
          <p className="text-muted-foreground text-sm">
            Where your clients sign in. Lowercase letters, numbers, and hyphens.
          </p>
          {errors.subdomain && (
            <p className="text-destructive text-sm">{errors.subdomain}</p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Icon</Label>
          <div className="flex items-center gap-4">
            {iconPreview ? (
              <img
                src={iconPreview}
                alt="Icon preview"
                className="size-12 rounded-md border object-cover"
              />
            ) : (
              <div className="bg-muted text-muted-foreground flex size-12 items-center justify-center rounded-md border">
                <ImageIcon className="size-5" />
              </div>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-sm"
              onClick={() => iconRef.current?.click()}
            >
              {iconPreview ? "Change icon" : "Upload icon"}
            </Button>
            <input
              ref={iconRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) {
                  setIconFile(f)
                  setIconPreview(URL.createObjectURL(f))
                }
              }}
            />
          </div>
          <p className="text-muted-foreground text-sm">
            A square mark used in the sidebar and browser tab.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Logo</Label>
          <div className="flex items-center gap-4">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Logo preview"
                className="h-12 w-auto max-w-[200px] rounded-md border object-contain p-1"
              />
            ) : (
              <div className="bg-muted text-muted-foreground flex h-12 w-32 items-center justify-center rounded-md border">
                <ImageIcon className="size-5" />
              </div>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-sm"
              onClick={() => logoRef.current?.click()}
            >
              {logoPreview ? "Change logo" : "Upload logo"}
            </Button>
            <input
              ref={logoRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) {
                  setLogoFile(f)
                  setLogoPreview(URL.createObjectURL(f))
                }
              }}
            />
          </div>
          <p className="text-muted-foreground text-sm">
            A full wordmark shown on your clients&rsquo; sign-in and onboarding
            screens.
          </p>
        </div>

        <Button
          className="w-full"
          disabled={!name.trim() || saving}
          loading={saving}
          onClick={submit}
        >
          Continue
        </Button>
      </div>
    </OnboardingShell>
  )
}

// ---------------------------------------------------------------------------
// Step 2 — Identity (IMP-464). Name + color the white-label assistant. The
// preview leads; color sits above the name so the orb the partner is looking
// at is already theirs. The name field opens pre-filled from the dice, so
// Continue always produces a named assistant.
// ---------------------------------------------------------------------------
interface IdentityProps {
  assistant_name: string | null
  assistant_color: string | null
  // The dominant color of the logo uploaded on the Brand step (nil when none
  // was detected); mapped onto the offered palette for the default selection.
  brand_color: string | null
}

function StepIdentity({ identity }: { identity: IdentityProps }) {
  const [name, setName] = useState(
    () => identity.assistant_name ?? rollAssistantName(),
  )
  const [color, setColor] = useState(
    () =>
      identity.assistant_color ??
      themeForBrandColor(identity.brand_color) ??
      "blue",
  )
  const [saving, setSaving] = useState(false)

  const orb = assistantTheme(color).colors

  function submit() {
    router.patch(
      partnerOnboardingIdentityPath(),
      { partner: { assistant_name: name.trim(), assistant_color: color } },
      {
        preserveScroll: true,
        onStart: () => setSaving(true),
        onFinish: () => setSaving(false),
      },
    )
  }

  return (
    <OnboardingShell
      step="identity"
      title="Name your assistant"
      subtitle="This is the main assistant taking care of business for your clients. Give it an identity based on your brand."
    >
      <div className="w-full space-y-6">
        {/* Live preview leads the step */}
        <div className="bg-muted/40 flex flex-col items-center justify-center gap-4 rounded-xl border px-8 py-10">
          <SiriOrb size={140} colors={orb} className="drop-shadow-xl" />
          <p className="text-sm font-semibold">{name.trim() || " "}</p>
        </div>

        <div className="space-y-2">
          <Label>Color</Label>
          <div className="flex flex-wrap items-center gap-2.5 pt-1">
            {ASSISTANT_THEMES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setColor(t.id)}
                aria-label={t.id}
                className={cn(
                  "size-8 rounded-full border-2 transition-transform",
                  color === t.id
                    ? "border-foreground scale-110"
                    : "border-transparent",
                )}
                style={{ backgroundColor: t.swatch }}
              />
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="assistant-name">Assistant name</Label>
          <div className="relative">
            <Input
              id="assistant-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setName(rollAssistantName(name))}
              aria-label="Roll a new name"
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2.5 -translate-y-1/2 transition-colors"
            >
              <Dices className="size-4" />
            </button>
          </div>
        </div>

        <Button
          className="w-full"
          onClick={submit}
          loading={saving}
          disabled={name.trim().length === 0}
        >
          Continue
        </Button>
      </div>
    </OnboardingShell>
  )
}

// ---------------------------------------------------------------------------
// Step 2 — Connect Stripe. The connect button is a native form POST to the real
// Stripe Connect endpoint (Inertia can't follow the cross-origin redirect); the
// connected state comes from the server after the OAuth round-trip.
// ---------------------------------------------------------------------------
function StepStripe({ connected }: { connected: boolean }) {
  const csrfToken =
    typeof document !== "undefined"
      ? (document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content") ?? "")
      : ""

  function advance() {
    router.post(
      partnerOnboardingStripeContinuePath(),
      {},
      { preserveScroll: true },
    )
  }

  return (
    <OnboardingShell
      step="stripe"
      title="Connect your payout account"
      subtitle="We use Stripe to bill your clients and pay you out. Connect an existing Stripe account or create one in a couple of minutes."
    >
      <div className="w-full space-y-6">
        <div className="bg-muted/30 flex items-start justify-between gap-4 rounded-lg border px-4 py-4">
          {connected ? (
            <>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">Stripe</span>
                <Badge variant="operational">Connected</Badge>
              </div>
              <Button
                asChild
                size="sm"
                variant="outline"
                className="rounded-sm"
              >
                <a
                  href="https://dashboard.stripe.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Stripe
                  <ExternalLink className="ml-1 size-3" />
                </a>
              </Button>
            </>
          ) : (
            <>
              <div className="flex flex-col text-sm">
                <span className="font-medium">No payout account</span>
                <span className="text-muted-foreground">
                  Connect an existing Stripe account, or sign up for a new one
                </span>
              </div>
              <form
                action={partnerSettingsStripeAccountPath()}
                method="POST"
                className="contents"
              >
                <input
                  type="hidden"
                  name="authenticity_token"
                  value={csrfToken}
                />
                <Button
                  type="submit"
                  size="sm"
                  variant="outline"
                  className="rounded-sm"
                >
                  Connect Stripe
                </Button>
              </form>
            </>
          )}
        </div>

        <ul className="text-muted-foreground space-y-2 text-sm">
          {[
            "Charge your clients on their agent subscriptions",
            "Receive automatic payouts on your markup",
            "Track invoices and payouts from your dashboard",
          ].map((line) => (
            <li key={line} className="flex items-start gap-2">
              <Check className="mt-0.5 size-4 shrink-0 text-green-600 dark:text-green-500" />
              {line}
            </li>
          ))}
        </ul>

        <div className="space-y-2">
          <Button className="w-full" disabled={!connected} onClick={advance}>
            Continue
          </Button>
          {!connected && (
            <Button
              type="button"
              variant="ghost"
              className="text-muted-foreground w-full"
              onClick={advance}
            >
              I&rsquo;ll do this later
            </Button>
          )}
        </div>
      </div>
    </OnboardingShell>
  )
}

// ---------------------------------------------------------------------------
// Step 3 — Markups
// ---------------------------------------------------------------------------
interface MarkupAgent {
  uid: string
  name: string
  icon: string
  outcome: string
  wholesale_price_cents: number
  markup_override_kind: OverrideType | null
  markup_override_value: string | number | null
}
interface MarkupsProps {
  default_markup_percentage: string | number
  agents: MarkupAgent[]
}
// IMP-420: partners start onboarding at a real margin, not 0% (which reads as
// "free"). The server already sends 50 until they choose their own; this is the
// belt-and-suspenders fallback if the prop is ever missing.
const ONBOARDING_DEFAULT_MARKUP = 50

function StepMarkups({ markups }: { markups: MarkupsProps }) {
  const [markupPct, setMarkupPct] = useState(
    Number(markups.default_markup_percentage) || ONBOARDING_DEFAULT_MARKUP,
  )
  const [saving, setSaving] = useState(false)

  // IMP-420 follow-up: onboarding only sets the default markup; per-agent
  // pricing is configured in partner Settings.
  function submit() {
    router.patch(
      partnerOnboardingMarkupsPath(),
      { default_markup_percentage: markupPct },
      {
        preserveScroll: true,
        onStart: () => setSaving(true),
        onFinish: () => setSaving(false),
      },
    )
  }

  const wholesaleExample = 140
  const clientExample =
    Math.ceil((wholesaleExample * (1 + markupPct / 100)) / 5) * 5

  return (
    <OnboardingShell
      step="markups"
      width="max-w-2xl"
      title="Set your pricing"
      subtitle="Choose the markup you add on top of wholesale cost. You can configure per-agent pricing in the settings."
    >
      <div className="w-full space-y-8">
        <div className="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="text-sm font-semibold">Default markup</h2>
              <p className="text-muted-foreground mt-1 text-sm">
                Applied to all agents. Fine-tune individual agents later in
                Settings.
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="w-32">
                <Input
                  className="text-sm tabular-nums"
                  suffix="%"
                  value={markupPct}
                  onChange={(e) => {
                    const raw = parseFloat(
                      e.target.value.replace(/[^0-9.]/g, ""),
                    )
                    setMarkupPct(isNaN(raw) ? 0 : raw)
                  }}
                />
              </div>
              <p className="text-muted-foreground text-sm">
                Prices round to the nearest $5 increment
              </p>
            </div>
          </div>

          <div className="bg-muted w-full space-y-3 rounded-lg border px-4 py-3 text-sm sm:max-w-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Wholesale cost</span>
              <span>${wholesaleExample}/mo</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Your markup ({markupPct.toFixed(0)}%)
              </span>
              <span className="text-green-600 dark:text-green-500">
                +${clientExample - wholesaleExample}/mo
              </span>
            </div>
            <div className="mt-1 flex justify-between border-t pt-2 font-medium">
              <span>Client price</span>
              <span>${clientExample}/mo</span>
            </div>
          </div>
        </div>

        <Button className="w-full" onClick={submit} loading={saving}>
          Continue
        </Button>
      </div>
    </OnboardingShell>
  )
}

// ---------------------------------------------------------------------------
// Step 4 — Invite admins
// ---------------------------------------------------------------------------
function StepAdmins() {
  const [invites, setInvites] = useState<string[]>([""])
  const [saving, setSaving] = useState(false)

  function update(i: number, value: string) {
    setInvites(invites.map((e, idx) => (idx === i ? value : e)))
  }
  function add() {
    setInvites([...invites, ""])
  }
  function remove(i: number) {
    setInvites(invites.filter((_, idx) => idx !== i))
  }

  const emails = invites.map((e) => e.trim()).filter(Boolean)
  const hasAny = emails.length > 0

  function finish() {
    router.post(
      partnerOnboardingFinishPath(),
      { emails },
      {
        preserveScroll: true,
        onStart: () => setSaving(true),
        onFinish: () => setSaving(false),
      },
    )
  }

  return (
    <OnboardingShell
      step="admins"
      title="Invite your team"
      subtitle="Add teammates who should help manage your accounts, agents, and billing. They'll join as admins with full access. You can invite more later."
    >
      <div className="w-full space-y-6">
        <div className="space-y-3">
          {invites.map((email, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                type="email"
                placeholder="teammate@example.com"
                value={email}
                onChange={(e) => update(i, e.target.value)}
                className="flex-1"
              />
              {invites.length > 1 && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="text-muted-foreground hover:text-destructive shrink-0"
                  onClick={() => remove(i)}
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
            </div>
          ))}
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={add}
        >
          <Plus className="size-3.5" />
          Add another
        </Button>

        <div className="space-y-2">
          <Button className="w-full" onClick={finish} loading={saving}>
            {hasAny ? "Send invites & finish" : "Finish setup"}
          </Button>
          {!hasAny && (
            <button
              type="button"
              onClick={finish}
              className="text-muted-foreground hover:text-foreground mx-auto block text-sm"
            >
              I&rsquo;ll invite my team later
            </button>
          )}
        </div>
      </div>
    </OnboardingShell>
  )
}

// ---------------------------------------------------------------------------
// Completion — a brief hand-off screen before the dashboard.
// ---------------------------------------------------------------------------
function StepDone({ dashboardPath }: { dashboardPath: string }) {
  useEffect(() => {
    const t = setTimeout(() => router.visit(dashboardPath), 1600)
    return () => clearTimeout(t)
  }, [dashboardPath])

  return (
    <div className="bg-background flex min-h-svh flex-col items-center justify-center px-6 text-center">
      <div className="bg-primary/10 mb-6 flex size-14 items-center justify-center rounded-full">
        <Sparkles className="text-primary size-6" />
      </div>
      <h1 className="text-2xl font-medium tracking-tight">
        You&rsquo;re all set
      </h1>
      <p className="text-muted-foreground mt-2 max-w-sm text-base/7">
        Taking you to your dashboard
        <span className="inline-flex align-middle">
          <Loader2 className="ml-2 size-4 animate-spin" />
        </span>
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Orchestrator — the current step is server-driven; each step advances it.
// ---------------------------------------------------------------------------
export default function PartnerOnboarding({
  step,
  brand,
  identity,
  stripe,
  markups,
  dashboard_path,
}: {
  step: Step
  brand: BrandProps
  identity: IdentityProps
  stripe: { connected: boolean; payouts_ready: boolean }
  markups: MarkupsProps
  dashboard_path: string
}) {
  return (
    <>
      <Head title="Set up your partner account — Imperative" />
      {step === "brand" && <StepBrand brand={brand} />}
      {step === "identity" && <StepIdentity identity={identity} />}
      {step === "stripe" && <StepStripe connected={stripe.connected} />}
      {step === "markups" && <StepMarkups markups={markups} />}
      {step === "admins" && <StepAdmins />}
      {step === "complete" && <StepDone dashboardPath={dashboard_path} />}
    </>
  )
}
